import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, filter, map } from 'rxjs';
import { NavPageInfo } from './model/page-info';
import { TranslatePipe } from '../shared/pipe/translate.pipe';

@Injectable({
  providedIn: 'root',
})
export class NavService {
  public currentUrl = new BehaviorSubject<any>(undefined);
  public navPagesChanged = new Subject<NavPageInfo[]>();
  private currentRoutePages: NavPageInfo[];
  constructor(
    private titleService: Title,
    private translate: TranslatePipe,
    private router: Router
  ) {
    // Track route changes and update currentUrl
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.router.url)
      )
      .subscribe((url) => {
        this.currentUrl.next(url);
      });

    // Initialize with current URL
    this.currentUrl.next(this.router.url);
  }

  public getRouterPagesInfo(
    router: Router,
    activatedRoute: ActivatedRoute
  ): Observable<NavPageInfo[]> {
    let obs = new Observable<NavPageInfo[]>((res) => {
      router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .pipe(map(() => activatedRoute))
        .pipe(
          map((route: ActivatedRoute) => {
            while (route.firstChild) {
              route = route.firstChild;
            }
            return route;
          })
        )
        .pipe(filter((route: ActivatedRoute) => route.outlet === 'primary'))
        .subscribe((currentRoute: ActivatedRoute) => {
          const pages = this.createCurrentRoutePages(currentRoute);
          this.currentRoutePages = pages;
          res.next(pages);
        });
    });
    return obs;
  }

  private createCurrentRoutePages(currentRoute: ActivatedRoute): NavPageInfo[] {
    let pages: NavPageInfo[] = [];
    if (currentRoute.routeConfig) {
      const routeData = currentRoute.routeConfig.data as any;
      if (routeData) {
        const currentRoutePageInfo: NavPageInfo = {
          title: routeData.title,
          url: currentRoute.routeConfig.path,
        };
        pages.push(currentRoutePageInfo);
        this.titleService.setTitle(
          this.translate.transform(currentRoutePageInfo.title)
        );

        if (currentRoute.routeConfig.path !== '' && currentRoute.parent) {
          const parentCfg = currentRoute.parent.routeConfig;
          if (parentCfg && parentCfg.children) {
            const parentRoute = parentCfg.children.find(
              (c) => (c.data as any)?.isList === true
            );
            if (parentRoute) {
              const uriFromRoot = this.buildRouteUriFromRoot(
                currentRoute.parent
              );
              const parentRoutePageInfo: NavPageInfo = {
                title: (parentRoute.data as any)?.title,
                url: uriFromRoot,
              };
              pages.splice(0, 0, parentRoutePageInfo);
            }
          }
        }
      }
    }
    return pages;
  }

  private buildRouteUriFromRoot(route: ActivatedRoute): string | undefined {
    let uri = '';
    if (route && route.pathFromRoot) {
      route.pathFromRoot.forEach((p) => {
        if (p.routeConfig) {
          if (p.routeConfig.path && p.routeConfig.path !== '') {
            uri += '/' + p.routeConfig.path;
          }
        }
      });
    }
    return uri;
  }

  public appentTextToCurrentRoute(text: string) {
    if (this.currentRoutePages && this.currentRoutePages.length > 0) {
      this.currentRoutePages[this.currentRoutePages.length - 1].title +=
        ' - ' + text;
      this.navPagesChanged.next(this.currentRoutePages);
    }
  }

  public addBreadcrumbBefore(title: string, url: string) {
    const pi: NavPageInfo = {
      title: title,
      url: url,
    };
    this.addBreadcrumbsBefore([pi]);
  }

  public addBreadcrumbsBefore(navItem: NavPageInfo[]) {
    if (
      this.currentRoutePages &&
      this.currentRoutePages.length > 0 &&
      navItem &&
      navItem.length > 0
    ) {
      let startIndex = 1;
      navItem.forEach((n) => {
        this.currentRoutePages.splice(
          this.currentRoutePages.length - startIndex++,
          0,
          n
        );
      });
      this.navPagesChanged.next(this.currentRoutePages);
    }
  }
}
